import React, { useState, useMemo, useEffect } from 'react';
import { useT } from '../i18n.js';
// ⚠ MODIFIED FROM PORT — KYC file uploads need base64 conversion
import { downloadComplianceDocument, downloadCsv, fileToBase64 } from '../api.js';
// ⚠ MODIFIED FROM PORT — browser-local "save draft" for the register form
import { useFormDraft, savedAgo } from '../drafts.js';
import { BILLING_STATUS_META, CATEGORIES, PENDING_APPROVALS, SUPPLIERS, SUPPLIER_STATUS_META, catOf, fmtTHB, netPayableOf, parseMoney } from './data.jsx';
import { MoneyInput } from './money-input.jsx';
import { IconArrow, IconBank, IconBuilding, IconCal, IconCheck, IconChevron, IconChevronDn, IconDots, IconDownload, IconExt, IconEye, IconFile, IconFilter, IconLayers, IconPlus, IconSearch, IconShield, IconSort, IconStar, IconTag, IconUpload, IconX } from './icons.jsx';
import { DetailTabContent } from './screens-flows.jsx';

/** Restore a single leading zero coerced off a Thai 13-digit tax/national ID.
 *  A 12-digit all-digit value is the exact signature of that numeric coercion
 *  (Sheets dropping the leading 0); anything else is returned unchanged so a
 *  genuinely malformed ID still fails the 13-digit check. */
export const padThaiId = (v) => {
  const d = String(v == null ? '' : v).replace(/\D/g, '');
  return d.length === 12 ? '0' + d : d;
};

/* ============================================================
   Shared bits
   ============================================================ */
export const Avatar = ({ name, size = "" }) => {
  // ⚠ FIX (2026-06-01) — guard against null/empty/whitespace name (live rows can
  // have a missing PM email or vendor name); .split on undefined white-screened the page.
  const initials = (String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('') || '?').toUpperCase();
  const sz = size === "lg" ? "avatar-lg" : size === "sm" ? "avatar-sm" : "";
  return <span className={"avatar " + sz}>{initials}</span>;
};

export const StatusPill = ({ meta }) => {
  const t = useT();
  if (!meta) return null;
  const cls = {
    success: "pill-success", warning: "pill-warning",
    danger:  "pill-danger",  info: "pill-info",
    neutral: "pill-neutral",
  }[meta.kind] || "pill-neutral";
  return (
    <span className={"pill " + cls}>
      <span className="dot"></span>
      {t(meta.label)}
    </span>
  );
};

export const CategoryTag = ({ id }) => {
  const t = useT();
  const c = catOf(id);
  if (!c) return null;
  return (
    <span className="cat">
      <i className="swatch" style={{background: c.color}}></i>
      {t(c.label)}
    </span>
  );
};

export const RatingBar = ({ value, max = 5 }) => (
  <span className="rating">
    <span className="rating-bar">
      {Array.from({length: max}).map((_, i) => (
        <i key={i} className={i < value ? "on" : ""}></i>
      ))}
    </span>
    <span className="tnum muted" style={{fontSize: 11}}>{value}.0</span>
  </span>
);

/* ⚠ NEW (2026-05-31) — reusable chip dropdown used by the Suppliers + Billing
 * toolbars to make the filter/sort/view controls actually work. Self-contained:
 * manages its own open state and closes on outside click. */
export function ChipMenu({ label, icon, value, options, onPick, align = "left", active }) {
  const [open, setOpen] = useState(false);
  const isActive = active !== undefined
    ? active
    : (value !== undefined && value !== null && value !== "any" && value !== "all" && value !== 0);
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button className={"chip " + (isActive ? "active" : "")} onClick={() => setOpen(o => !o)}>
        {icon}{label}<IconChevronDn size={10} style={{ opacity: 0.55, marginLeft: 2 }}/>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }}/>
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", [align]: 0, zIndex: 201,
            minWidth: 190, background: "var(--bg-elev-2, var(--bg-secondary))",
            border: "1px solid var(--border-default)", borderRadius: 10,
            boxShadow: "var(--elevation-3)", padding: 4, maxHeight: 340, overflowY: "auto",
          }}>
            {options.map(o => (
              <button key={String(o.value)} onClick={() => { onPick(o.value); setOpen(false); }}
                style={{
                  display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 10,
                  padding: "8px 10px", background: o.value === value ? "var(--bg-elev-1, rgba(255,255,255,0.06))" : "transparent",
                  border: "none", borderRadius: 6, cursor: "pointer", color: "var(--text-primary)",
                  fontSize: 13, fontFamily: "var(--font-en)", textAlign: "left",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elev-1, rgba(255,255,255,0.06))"}
                onMouseLeave={e => e.currentTarget.style.background = o.value === value ? "var(--bg-elev-1, rgba(255,255,255,0.06))" : "transparent"}>
                <span>{o.label}</span>
                {o.value === value && <IconCheck size={12}/>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   SCREEN 1 — Supplier Database
   ============================================================ */
export function ScreenDatabase({ onOpenSupplier, onNewSupplier, vendors, invoices = [], canCreate = true }) {
  const t = useT();
  // ⚠ MODIFIED FROM PORT — `vendors` prop added so App can pass real data
  // from api.listVendors(). `vendors === null` means "still loading" → show a
  // loading state, NOT mock data. Backend returns rows with `vendor_id`.
  const loading = vendors === null;
  const source = (vendors || []).map(v => v.vendor_id ? { ...v, id: v.vendor_id } : v);
  // ⚠ FIX (2026-07-19) — Spend YTD + Proj. were reading `v.spend`/`v.projects`,
  // which api.listVendors() does NOT return (they're not in the vendor master),
  // so every row showed ฿0 / 0. Derive them per-vendor from the live invoices —
  // same definitions the supplier-detail screen uses: Spend YTD = net-payable of
  // invoices paid this calendar year; Proj. = distinct POs in the last 12 months.
  const spendByVendor = useMemo(() => {
    const thisYear  = new Date().getFullYear();
    const since12mo = Date.now() - 365 * 86400000;
    const acc = {};
    for (const i of (invoices || [])) {
      const vid = i.vendor_id || i.vid;
      if (!vid) continue;
      const e = acc[vid] || (acc[vid] = { spend: 0, pos: new Set() });
      if (i.status === 'paid' && i.paid_at && new Date(i.paid_at).getFullYear() === thisYear) {
        e.spend += netPayableOf(i);
      }
      const po = i.po_number || i.po;
      if (po && i.submitted_at && new Date(i.submitted_at).getTime() >= since12mo) e.pos.add(po);
    }
    const out = {};
    for (const k in acc) out[k] = { spend: acc[k].spend, projects: acc[k].pos.size };
    return out;
  }, [invoices]);
  const spendOf = (s) => (spendByVendor[s.vendor_id || s.id] || {}).spend || 0;
  const projOf  = (s) => (spendByVendor[s.vendor_id || s.id] || {}).projects || 0;
  const [activeCats, setActiveCats] = useState(new Set());
  const [query, setQuery] = useState("");
  // ⚠ NEW (2026-05-31) — working toolbar: terms / rating / status filters,
  // sort, density, and an expandable category list.
  const [termsFilter, setTermsFilter] = useState("any");
  const [minRating, setMinRating] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  // ⚠ NEW (2026-07-19) — sort is now driven by clickable column headers with an
  // asc/desc toggle. The toolbar "Sort" dropdown shares the same sortKey/sortDir.
  const [sortKey, setSortKey] = useState("updated");
  const [sortDir, setSortDir] = useState("desc");   // "updated" starts newest-first
  const [density, setDensity] = useState("comfortable");
  const [showAllCats, setShowAllCats] = useState(false);
  // Natural default direction per column (so the dropdown reads correctly and a
  // fresh click on a numeric column starts high→low).
  const DEFAULT_DIR = { id: "asc", name: "asc", category: "asc", status: "asc", terms: "asc", rating: "desc", spend: "desc", projects: "desc", updated: "desc" };

  const filtered = useMemo(() => {
    const out = source.filter(s => {
      if (activeCats.size && !activeCats.has(s.cat)) return false;
      if (termsFilter !== "any" && String(s.terms || "").toUpperCase() !== termsFilter) return false;
      if (minRating > 0 && Number(s.rating || 0) < minRating) return false;
      // Archived suppliers are hidden from the default list — reachable only via
      // the explicit "Archived" status filter.
      if (statusFilter === "all") { if (s.status === "archived") return false; }
      else if (s.status !== statusFilter) return false;
      if (query && !(s.name.toLowerCase().includes(query.toLowerCase()) ||
                     s.id.toLowerCase().includes(query.toLowerCase()))) return false;
      return true;
    });
    const catName  = (s) => (CATEGORIES.find(c => c.id === s.cat) || {}).label || s.cat || "";
    const netTerms = (s) => Number(String(s.terms || "").replace(/[^\d]/g, "")) || 0;
    // Base comparators are all ascending; sortDir applies the direction. This
    // keeps every column click a simple asc↔desc toggle.
    const baseCmp = {
      id:       (a, b) => String(a.id).localeCompare(String(b.id)),
      name:     (a, b) => String(a.name).localeCompare(String(b.name)),
      category: (a, b) => catName(a).localeCompare(catName(b)),
      status:   (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
      terms:    (a, b) => netTerms(a) - netTerms(b),
      rating:   (a, b) => Number(a.rating || 0) - Number(b.rating || 0),
      spend:    (a, b) => spendOf(a) - spendOf(b),
      projects: (a, b) => projOf(a) - projOf(b),
      updated:  (a, b) => String(a.updated_at || "").localeCompare(String(b.updated_at || "")),
    }[sortKey];
    if (!baseCmp) return out;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...out].sort((a, b) => dir * baseCmp(a, b));
  }, [source, activeCats, termsFilter, minRating, statusFilter, query, sortKey, sortDir, spendByVendor]);

  // Toolbar dropdown → set key + its natural direction. Header click → toggle
  // direction if it's the active column, otherwise switch to it.
  const pickSort  = (k) => { setSortKey(k); setSortDir(DEFAULT_DIR[k] || "asc"); };
  const clickSort = (k, e) => {
    // Ignore clicks landing on the resize edge (right ~7px) so grabbing the
    // column-resize handle doesn't also re-sort.
    if (e) { const r = e.currentTarget.getBoundingClientRect(); if (r.right - e.clientX <= 7) return; }
    if (k === sortKey) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else pickSort(k);
  };
  const sortArrow = (k) => {
    if (sortKey !== k) return <IconChevronDn size={10} style={{ opacity: 0.22 }}/>;
    return <IconChevronDn size={10} style={sortDir === "asc" ? { transform: "rotate(180deg)" } : undefined}/>;
  };

  const toggleCat = (id) => {
    const n = new Set(activeCats);
    n.has(id) ? n.delete(id) : n.add(id);
    setActiveCats(n);
  };
  const anyFilter = activeCats.size > 0 || termsFilter !== "any" || minRating > 0 || statusFilter !== "all" || !!query;
  const clearAll = () => { setActiveCats(new Set()); setTermsFilter("any"); setMinRating(0); setStatusFilter("all"); setQuery(""); };
  const STATUS_OPTS = [
    { value: "all", label: t("All statuses") },
    { value: "active", label: t("Active") },
    { value: "review", label: t("In review") },
    { value: "blacklist", label: t("Blacklisted") },
    { value: "archived", label: t("Archived") },
  ];
  const TERMS_OPTS = ["any", "NET 7", "NET 15", "NET 30", "NET 45", "NET 60", "NET 90"]
    .map(tm => ({ value: tm, label: tm === "any" ? t("Any terms") : tm }));
  const RATING_OPTS = [
    { value: 0, label: t("Any rating") },
    { value: 3, label: t("3.0 and up") },
    { value: 4, label: t("4.0 and up") },
    { value: 4.5, label: t("4.5 and up") },
  ];
  const SORT_OPTS = [
    { value: "updated", label: t("Recently updated") },
    { value: "name", label: t("Name (A–Z)") },
    { value: "rating", label: t("Rating (high→low)") },
    { value: "spend", label: t("Spend (high→low)") },
    { value: "projects", label: t("Projects (high→low)") },
  ];
  const VIEW_OPTS = [
    { value: "comfortable", label: t("Comfortable") },
    { value: "compact", label: t("Compact") },
  ];
  const catLabel = (id) => (CATEGORIES.find(c => c.id === id) || {}).label || id;
  const statusLabel = (STATUS_OPTS.find(o => o.value === statusFilter) || {}).label;
  const ratingLabel = (RATING_OPTS.find(o => o.value === minRating) || {}).label;
  const sortLabel = (SORT_OPTS.find(o => o.value === sortKey) || {}).label;
  const extraCats = Math.max(CATEGORIES.length - 6, 0);

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Suppliers")}</h1>
          <div className="sub">{loading ? t('Loading…') : t('{n} vendors', { n: source.length })} · {t('indexed by Procurement')}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" disabled={!filtered.length}
            onClick={() => downloadCsv(`suppliers-${new Date().toISOString().slice(0,10)}.csv`, filtered, [
              { label: 'Vendor ID', key: 'id' },
              { label: 'Name', key: 'name' },
              { label: 'Category', get: s => (CATEGORIES.find(c => c.id === s.cat) || {}).label || s.cat },
              { label: 'Status', key: 'status' },
              { label: 'Terms', key: 'terms' },
              { label: 'Compliance', key: 'compliance' },
              { label: 'Rating', key: 'rating' },
              { label: 'Spend YTD (THB)', get: s => spendOf(s) },
              { label: 'Projects (12mo)', get: s => projOf(s) },
              { label: 'Contact', key: 'contact' },
              { label: 'Email', key: 'email' },
              { label: 'Phone', key: 'phone' },
            ])}>
            <IconDownload size={14}/> {t("Export")}
          </button>
          {canCreate && (
            <button className="btn btn-primary btn-sm" onClick={onNewSupplier}><IconPlus size={14}/> {t("New supplier")}</button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <ChipMenu label={t('Status: {v}', { v: statusLabel })} icon={<IconFilter size={12} style={{marginRight:6}}/>}
          value={statusFilter} options={STATUS_OPTS} onPick={setStatusFilter}/>
        <span className="divider"></span>
        <span className="muted" style={{fontSize:11, textTransform:"uppercase", letterSpacing:"0.08em"}}>{t('Category')}</span>
        {(showAllCats ? CATEGORIES : CATEGORIES.slice(0, 6)).map(c => (
          <button key={c.id} className={"chip " + (activeCats.has(c.id) ? "active" : "")}
            onClick={() => toggleCat(c.id)}>
            <i style={{display:"inline-block", width: 8, height: 8, borderRadius: 2, background: c.color}}></i>
            {t(c.label)}
            {activeCats.has(c.id) && <span className="x"><IconX size={10}/></span>}
          </button>
        ))}
        {extraCats > 0 && (
          <button className="chip muted" onClick={() => setShowAllCats(v => !v)}>
            {showAllCats ? t('Show less') : t('+{n} more', { n: extraCats })}
          </button>
        )}
        <span className="divider"></span>
        <ChipMenu label={termsFilter === "any" ? t('Terms: any') : t('Terms: {v}', { v: termsFilter })}
          value={termsFilter} options={TERMS_OPTS} onPick={setTermsFilter}/>
        <ChipMenu label={minRating > 0 ? t('Rating {v}', { v: ratingLabel }) : t('Rating: any')}
          value={minRating} options={RATING_OPTS} onPick={setMinRating}/>
        {anyFilter && (
          <button className="chip" onClick={clearAll}><IconX size={11}/> {t('Clear')}</button>
        )}
        <div className="toolbar-right">
          <div className="topbar-search" style={{maxWidth: 220, margin: 0}}>
            <IconSearch size={14} className="icon"/>
            <input placeholder={t('Search suppliers…')} value={query} onChange={e => setQuery(e.target.value)}/>
          </div>
          <ChipMenu label={t('Sort')} icon={<IconSort size={12} style={{marginRight:6}}/>} align="right"
            value={sortKey} options={SORT_OPTS} onPick={pickSort} active={sortKey !== "updated"}/>
          <ChipMenu label={t('View')} icon={<IconLayers size={12} style={{marginRight:6}}/>} align="right"
            value={density} options={VIEW_OPTS} onPick={setDensity} active={density !== "comfortable"}/>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className={"tbl" + (density === "compact" ? " tbl-compact" : "")} style={{tableLayout: "fixed", width: "100%"}}>
          <colgroup>
            <col style={{width: 92}}/>
            <col/>
            <col style={{width: 124}}/>
            <col style={{width: 92}}/>
            <col style={{width: 80}}/>
            <col style={{width: 102}}/>
            <col style={{width: 120}}/>
            <col style={{width: 66}}/>
            <col style={{width: 100}}/>
          </colgroup>
          <thead>
            <tr>
              {[
                ["id",       t('ID'),        false],
                ["name",     t('Supplier'),  false],
                ["category", t('Category'),  false],
                ["status",   t('Status'),    false],
                ["terms",    t('Terms'),     false],
                ["rating",   t('Rating'),    false],
                ["spend",    t('Spend YTD'), true],
                ["projects", t('Proj.'),     true],
                ["updated",  t('Updated'),   false],
              ].map(([k, label, right]) => (
                <th key={k} className={right ? "right" : undefined}
                  onClick={(e) => clickSort(k, e)}
                  aria-sort={sortKey === k ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  title={t('Sort by {c}', { c: label })}
                  style={{ cursor: "pointer" }}>
                  <span className="sort" style={right ? { justifyContent: "flex-end" } : undefined}>
                    {label} {sortArrow(k)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>{t('Loading suppliers…')}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} style={{padding: 48, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>{t('No suppliers match your filters.')}</td></tr>
            ) : filtered.map(s => (
              <tr key={s.id}>
                <td className="mono muted" style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{s.id}</td>
                <td style={{overflow:"hidden"}}>
                  <a href="#" onClick={(e) => { e.preventDefault(); onOpenSupplier(s); }}
                    style={{color:"var(--text-primary)", textDecoration:"none", display:"flex", gap:10, alignItems:"center", minWidth:0}}>
                    <Avatar name={s.name} size="sm"/>
                    <span className="ellipsis">{s.name}</span>
                  </a>
                </td>
                <td style={{overflow:"hidden"}}><CategoryTag id={s.cat}/></td>
                <td><StatusPill meta={SUPPLIER_STATUS_META[s.status]}/></td>
                <td className="mono">{s.terms}</td>
                <td><RatingBar value={s.rating}/></td>
                <td className="num right">{fmtTHB(spendOf(s))}</td>
                <td className="num right muted">{projOf(s)}</td>
                <td className="muted" style={{fontSize: 12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{s.updated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="status-bar">
        <span>{t('{a} of {b} rows', { a: filtered.length, b: source.length })}</span>
        <span style={{marginLeft:"auto"}}>{t('Sum spend: {v}', { v: fmtTHB(filtered.reduce((a,b) => a + spendOf(b), 0)) })}</span>
        <span>·</span>
        <span>{t('Avg rating: {v}', { v: (filtered.reduce((a,b) => a+b.rating, 0) / (filtered.length||1)).toFixed(1) })}</span>
      </div>
    </div>
  );
}

/* ============================================================
   SCREEN 2 — Supplier Detail
   ============================================================ */
export function ScreenDetail({ supplier, onBack, onIssueMagicLink, canIssueMagicLink, onIssueKycLink, canIssueKycLink, onEdit, canEdit, onArchive, onDelete, canArchive, invoices = [], ratings = [], audits = [], users = [], compliance = [] }) {
  // ⚠ MODIFIED FROM PORT — `onIssueMagicLink`, `onEdit` + role flags;
  // also accepts live per-vendor invoices/ratings/audits/users so the
  // stat tiles and sub-tabs show real data instead of prototype mocks.
  const t = useT();
  const s = supplier || SUPPLIERS[0];
  const vid = s.vendor_id || s.id;
  const [tab, setTab] = useState("overview");
  const [issuing, setIssuing] = useState(false);
  const issue = async () => {
    setIssuing(true);
    try { await onIssueMagicLink?.(s.id || s.vendor_id); }
    finally { setIssuing(false); }
  };
  const [issuingKyc, setIssuingKyc] = useState(false);
  const issueKyc = async () => {
    setIssuingKyc(true);
    try { await onIssueKycLink?.(s.id || s.vendor_id, s.name); }
    finally { setIssuingKyc(false); }
  };
  // ----- live per-vendor metrics -----
  const myInvoices = (invoices || []).filter(i => (i.vendor_id || i.vid) === vid);
  const myRatings  = (ratings  || []).filter(r => r.vendor_id === vid);
  const myCompliance = (compliance || []).filter(c => c.vendor_id === vid);
  const thisYear   = new Date().getFullYear();
  const isPaidThisYear = (i) => i.status === 'paid' && i.paid_at && new Date(i.paid_at).getFullYear() === thisYear;
  const spendYtd   = myInvoices.filter(isPaidThisYear).reduce((a, b) => a + netPayableOf(b), 0);
  // Distinct POs in the last 12 months — proxy for "projects"
  const since12mo  = Date.now() - 365 * 86400000;
  const recentPOs  = new Set(
    myInvoices
      .filter(i => i.submitted_at && new Date(i.submitted_at).getTime() >= since12mo && (i.po_number || i.po))
      .map(i => i.po_number || i.po)
  );
  const activePOs  = new Set(
    myInvoices
      .filter(i => ['pm-review','finance-verify','approved-payment','scheduled'].includes(i.status) && (i.po_number || i.po))
      .map(i => i.po_number || i.po)
  );
  // On-time delivery = paid invoices where paid_at ≤ due_at, over total paid
  const paidInv    = myInvoices.filter(i => i.status === 'paid' && i.paid_at);
  const onTimePaid = paidInv.filter(i => !i.due_at || new Date(i.paid_at).getTime() <= new Date(i.due_at).getTime());
  const onTimePct  = paidInv.length ? Math.round((onTimePaid.length / paidInv.length) * 100) : null;
  // Quality score from the Ratings sheet
  const scored     = myRatings.filter(r => Number(r.overall_5) > 0);
  const qualityAvg = scored.length
    ? (scored.reduce((a, b) => a + Number(b.overall_5), 0) / scored.length)
    : null;

  return (
    <div className="page">
      <div className="page-header" style={{alignItems:"center"}}>
        <div style={{display:"flex", gap: 16, alignItems:"center"}}>
          <button className="icon-btn" onClick={onBack} style={{transform:"rotate(180deg)"}}><IconArrow size={16}/></button>
          <Avatar name={s.name} size="lg"/>
          <div className="page-title-group">
            <h1 style={{display:"flex", alignItems:"center", gap: 10}}>
              {s.name}
              <StatusPill meta={SUPPLIER_STATUS_META[s.status]}/>
              <span className="pill pill-neutral" style={{fontSize: 11}}>
                {t(s.vendor_type === 'individual' ? 'Individual' : 'Company')}
              </span>
            </h1>
            <div className="sub" style={{display:"flex", gap: 16, alignItems:"center", flexWrap:"wrap"}}>
              <span className="mono">{s.id}</span>
              <span>·</span>
              <CategoryTag id={s.cat}/>
              <span>·</span>
              <span>{s.city || '—'}, {s.country || '—'}</span>
              {s.created_at && (<>
                <span>·</span>
                <span>{t('Added {date}', { date: new Date(s.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) })}</span>
              </>)}
              <span>·</span>
              <span>
                PM:&nbsp;
                {s.assigned_pm_email
                  ? <span className="mono" style={{color:"var(--text-secondary)"}}>{s.assigned_pm_email}</span>
                  : <span className="muted" style={{fontStyle:"italic"}}>{t('unassigned')}</span>}
              </span>
            </div>
          </div>
        </div>
        <div className="page-actions">
          {/* ⚠ MODIFIED (2026-07-19) — standalone "Verify KYC"/"Revoke KYC"
              actions removed. Approving a supplier in the Approval queue now
              verifies KYC in one step, so there is no separate KYC gate. */}
          {canIssueKycLink && s.compliance !== 'verified' && (
            <button className="btn btn-secondary btn-sm" onClick={issueKyc} disabled={issuingKyc}
              title={t('Copy a KYC-upload link and email it to the supplier')}>
              <IconShield size={14}/> {issuingKyc ? t('Issuing…') : t('Send KYC link')}
            </button>
          )}
          {canIssueMagicLink && (
            <button className="btn btn-secondary btn-sm" onClick={issue} disabled={issuing}>
              {issuing ? t('Issuing…') : t('Send portal link')}
            </button>
          )}
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => onEdit?.(s)}>{t('Edit profile')}</button>
          )}
          {canArchive && s.status === 'archived' && (
            <button className="btn btn-secondary btn-sm" onClick={() => onArchive?.(s, true)}>{t('Unarchive')}</button>
          )}
          {canArchive && s.status !== 'archived' && (
            <button className="btn btn-ghost btn-sm" onClick={() => onArchive?.(s, false)}
              title={t('Hide this supplier from the list. Reversible — keeps all history.')}>
              {t('Archive')}
            </button>
          )}
          {canArchive && (
            <button className="btn btn-ghost btn-sm" style={{color: 'var(--danger)'}}
              disabled={myInvoices.length > 0}
              title={myInvoices.length > 0
                ? t('This supplier has invoices — archive it instead to preserve finance history.')
                : t('Permanently delete this supplier (no invoices on file).')}
              onClick={() => onDelete?.(s)}>
              {t('Delete')}
            </button>
          )}
        </div>
      </div>

      <div className="tabs">
        {/* ⚠ MODIFIED FROM PORT — Compliance tab removed (per user request);
            tab counts now reflect live per-vendor data instead of prototype hardcoding. */}
        {[
          ["overview","Overview", null],
          ["docs","Documents",    myInvoices.reduce((a, b) => a + (b.pop_count || 0) + (b.has_pdf ? 1 : 0), 0) + myCompliance.filter(c => c.drive_file_id).length],
          ["projects","Past projects", recentPOs.size],
          ["billing","Billing history", myInvoices.length],
          ["audit","Audit log",   (audits || []).filter(a => a.entity_id === vid || (a.after_json || '').includes(vid)).length],
        ].map(([id, lbl, count]) => (
          <button key={id} className={"tab " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
            {t(lbl)}
            {count !== null && <span className="muted" style={{marginLeft: 6, fontSize: 11}}>{count}</span>}
          </button>
        ))}
      </div>

      {tab !== "overview" ? (
        <DetailTabContent
          tab={tab}
          supplier={s}
          invoices={myInvoices}
          ratings={myRatings}
          audits={audits}
          users={users}
          compliance={myCompliance}
        />
      ) : (<>
      <div className="stat-row" style={{marginTop: 16}}>
        <div className="stat-tile">
          <div className="label">{t('Spend YTD')}</div>
          <div className="val">{fmtTHB(spendYtd)}</div>
          <div className="delta muted">{t('{n} paid · {year}', { n: paidInv.filter(i => new Date(i.paid_at).getFullYear() === thisYear).length, year: thisYear })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Projects (12 mo)')}</div>
          <div className="val">{recentPOs.size}</div>
          <div className="delta muted">{t('{n} active', { n: activePOs.size })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('On-time delivery')}</div>
          <div className="val">{onTimePct === null ? '—' : <>{onTimePct}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>%</span></>}</div>
          <div className="delta muted">{t('{n} paid invoices', { n: paidInv.length })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Quality score')}</div>
          <div className="val">{qualityAvg === null ? '—' : <>{qualityAvg.toFixed(1)}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>/5</span></>}</div>
          <div className="delta muted">{t('{n} reviews', { n: scored.length })}</div>
        </div>
      </div>

      <div className="detail">
        <div className="detail-main">
          {/* ⚠ MODIFIED FROM PORT — live company + banking, account number unmasked */}
          <div className="section">
            <div className="section-head"><h3>{t('Company & banking')}</h3></div>
            <div className="section-body">
              <dl className="kv">
                <dt>{t('Company name')}</dt><dd>{s.name || '—'}</dd>
                <dt>{t('Tax ID / VAT')}</dt><dd><span className="mono">{padThaiId(s.tax_id || s.taxId) || '—'}</span></dd>
                <dt>{t('City / Country')}</dt><dd>{s.city || '—'}, {s.country || '—'}</dd>
                <dt>{t('Address')}</dt><dd style={{whiteSpace:"pre-wrap"}}>{s.address || '—'}</dd>
                <dt>{t('Credit terms')}</dt><dd><span className="mono">{s.terms || '—'}</span></dd>
                <dt>{t('Credit limit')}</dt><dd>{(s.credit_limit || s.creditLimit) ? `THB ${Number(s.credit_limit || s.creditLimit).toLocaleString()}` : '—'}</dd>
                <dt>{t('Bank')}</dt><dd>{s.bank_name || '—'}</dd>
                <dt>{t('Account number')}</dt><dd className="mono">{s.account_number || '—'}</dd>
                <dt>{t('Account name')}</dt><dd>{s.account_name || '—'}</dd>
              </dl>
            </div>
          </div>

          {/* ⚠ Recent activity + Documents sections removed (were prototype hardcoded).
              Real activity is in the Audit log screen; real KYC docs are on the Compliance tab
              and shown in the Approval queue review panel. */}
        </div>

        <div className="detail-aside">
          {/* ⚠ MODIFIED FROM PORT — primary contact uses live phone + line_id; Tags + Internal owner removed */}
          <div className="section">
            <div className="section-head"><h3>{t('Primary contact')}</h3></div>
            <div className="section-body">
              <div style={{display:"flex", gap: 12, alignItems:"center", marginBottom: 12}}>
                <Avatar name={s.contact || s.name || '?'} size="lg"/>
                <div>
                  <div style={{fontSize: 14, fontWeight: 400}}>{s.contact || '—'}</div>
                  <div style={{fontSize: 12, color:"var(--text-tertiary)"}}>{s.name || ''}</div>
                </div>
              </div>
              <dl className="kv" style={{gridTemplateColumns:"90px 1fr"}}>
                <dt>{t('Email')}</dt><dd className="mono" style={{fontSize: 12}}>{s.email || '—'}</dd>
                <dt>{t('Phone')}</dt><dd className="mono">{s.phone || '—'}</dd>
                <dt>{t('LINE')}</dt><dd className="mono">{s.line_id || '—'}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}

/* ============================================================
   SCREEN 3 — New Supplier Registration
   ============================================================ */
// ⚠ MODIFIED FROM PORT — full KYC registration form: company info, banking,
// credit limit, and 5 KYC documents (3 required, 2 optional).
/* ⚠ MODIFIED FROM PORT — shared "resume your saved draft?" banner. Rendered at
   the top of any form wired to the local-draft layer (Register supplier,
   Submit invoice). Imported by screens-portal.jsx too. */
export function DraftRestoreBanner({ t, savedAt, onRestore, onDiscard }) {
  return (
    <div style={{
      margin: "0 24px 4px", padding: "12px 16px",
      background: "var(--accent-soft, #efe9fc)",
      border: "1px solid var(--accent)", borderRadius: 10,
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)" }}>
          {t("Resume your saved draft?")}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2, lineHeight: 1.5 }}>
          {t("Saved {when}. Restore to keep editing, or discard to start fresh. Uploaded files aren't part of a draft — re-attach them.", { when: savedAgo(savedAt, t) })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onDiscard}>{t("Discard")}</button>
        <button className="btn btn-primary btn-sm" onClick={onRestore}>{t("Restore draft")}</button>
      </div>
    </div>
  );
}

// Pristine register form — also the baseline the draft layer compares against to
// decide whether the form is still "blank" (and therefore not worth saving).
const REGISTER_DEFAULTS = {
  vendor_type: "company",
  name: "", cat: "prod", contact: "", email: "", phone: "", line_id: "",
  tax_id: "", terms: "NET 30", notes: "", address: "",
  city: "", country: "TH",
  credit_limit: "",
};

export function ScreenRegister({ onCancel, onSubmit, draftKey }) {
  const t = useT();
  const [form, setForm] = useState(REGISTER_DEFAULTS);
  // ⚠ MODIFIED FROM PORT — browser-local draft (text fields only; uploaded KYC
  // docs are not part of a draft and are re-attached on resume).
  const { pendingDraft, restore, discard, saveNow, clear: clearDraft, savedAt } =
    useFormDraft({ key: draftKey, form, setForm, defaults: REGISTER_DEFAULTS });
  const [docs, setDocs] = useState({
    business_registration: null, vat_cert: null, national_id: null, book_bank: null,
    rate_card: null, company_profile: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setDoc = (k) => (e) => setDocs(d => ({ ...d, [k]: e.target.files?.[0] || null }));
  const isIndividual = form.vendor_type === 'individual';

  const submit = async () => {
    setError(null);
    const requiredText = [
      ['name',           isIndividual ? 'Full name' : 'Company name'],
      ['cat',            'Category'],
      ['contact',        'Primary contact'],
      ['email',          'Contact email'],
      ['phone',          'Phone'],
      ['tax_id',         isIndividual ? 'National ID' : 'Tax ID'],
      ['terms',          'Credit terms'],
      ['credit_limit',   'Credit limit'],
    ];
    for (const [k, label] of requiredText) {
      if (!String(form[k] || '').trim()) { setError(`${label} is required.`); return; }
    }
    if (!/^\d{13}$/.test(form.tax_id.replace(/\D/g, ''))) {
      setError(`${isIndividual ? 'National ID' : 'Tax ID'} must be 13 digits.`); return;
    }
    if (!(parseMoney(form.credit_limit) > 0)) {
      setError('Credit limit must be a number greater than zero.'); return;
    }
    // KYC documents are OPTIONAL here — the team can attach them now, or skip and
    // send the supplier a KYC link to upload them (offered after submit).

    setSubmitting(true);
    try {
      // base64-encode every uploaded doc (≤20MB each enforced backend-side)
      const payload = { ...form, credit_limit: parseMoney(form.credit_limit) };
      for (const k of Object.keys(docs)) {
        if (docs[k]) {
          if (docs[k].size > 20 * 1024 * 1024) throw new Error(`${k} file >20MB`);
          payload[k] = {
            base64: await fileToBase64(docs[k]),
            filename: docs[k].name,
          };
        }
      }
      await onSubmit?.(payload);
      clearDraft();          // submitted — drop the saved draft
      onCancel?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveDraft = () => {
    if (saveNow()) window.__toast?.(t('Draft saved'), 'success');
  };

  const req = <span style={{color:"var(--danger)"}}>*</span>;
  const DocField = ({ name, label, required }) => (
    <div className="field">
      <label className="field-label">{label} {required && req}</label>
      <input type="file" accept="application/pdf,image/*" onChange={setDoc(name)}
        style={{ display: 'block', padding: 6, fontSize: 12 }}/>
      {docs[name] && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-tertiary)' }}>
          {docs[name].name} · {(docs[name].size / 1024).toFixed(0)} KB
        </div>
      )}
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Register new supplier")}</h1>
          <div className="sub">{t("Your team enters company info; KYC docs can be uploaded by the supplier via a link.")}</div>
        </div>
        <div className="page-actions">
          {savedAt && !pendingDraft && (
            <span className="muted" style={{fontSize: 11, alignSelf: "center", marginRight: 4, whiteSpace: "nowrap"}}>
              {t("Draft saved · {when}", { when: savedAgo(savedAt, t) })}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onSaveDraft} disabled={submitting}>{t("Save draft")}</button>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={submitting}>{t("Cancel")}</button>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting}>
            {submitting ? t('Submitting…') : <>{t('Submit for approval')} <IconArrow size={12}/></>}
          </button>
        </div>
      </div>

      {pendingDraft && (
        <DraftRestoreBanner t={t} savedAt={pendingDraft.savedAt} onRestore={restore} onDiscard={discard}/>
      )}

      <div style={{padding: "8px 24px 24px", display: "grid", gridTemplateColumns: "1fr 320px", gap: 24}}>
        <div style={{display:"flex", flexDirection:"column", gap: 16}}>

          <div className="section">
            <div className="section-head"><h3>1 · {t("Supplier information")}</h3>
              <span className="muted" style={{fontSize: 12}}>{t("Entered by your team.")}</span>
            </div>
            <div className="section-body">
              <div className="form-grid">
                <div className="field full">
                  <label className="field-label">{t("Supplier type")} {req}</label>
                  <div style={{display:"flex", gap: 8}}>
                    {[["company","Registered company"],["individual","Individual"]].map(([val, lbl]) => (
                      <button key={val} type="button"
                        className={"btn btn-sm " + (form.vendor_type === val ? "btn-primary" : "btn-ghost")}
                        style={{flex: 1, justifyContent: "center", border: "1px solid var(--border-subtle)"}}
                        onClick={() => setForm(f => ({ ...f, vendor_type: val }))}>
                        {t(lbl)}
                      </button>
                    ))}
                  </div>
                  <div className="muted" style={{fontSize: 11, marginTop: 6}}>
                    {isIndividual
                      ? t('Individual supplier — KYC docs: National ID copy + bank book.')
                      : t('Registered company — KYC docs: business registration + bank book (VAT optional).')}
                  </div>
                </div>
                <div className="field full">
                  <label className="field-label">{isIndividual ? t('Full name') : t('Company name')} {req}</label>
                  <input className="input" value={form.name} onChange={set('name')}
                    placeholder={isIndividual ? 'Somchai Jaidee' : 'Tinker Post Bangkok'}/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Primary category")} {req}</label>
                  <div className="select">
                    <select value={form.cat} onChange={set('cat')}>
                      {CATEGORIES.map(c => <option key={c.id} value={c.id}>{t(c.label)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">{t("Credit terms")} {req}</label>
                  <div className="select">
                    <select value={form.terms} onChange={set('terms')}>
                      {["NET 7","NET 15","NET 30","NET 45","NET 60","NET 90"].map(tm => <option key={tm}>{tm}</option>)}
                    </select>
                  </div>
                </div>
                <div className="field">
                  <label className="field-label">{t("Primary contact")} {req}</label>
                  <input className="input" value={form.contact} onChange={set('contact')} placeholder="Praew Wattana"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Contact email")} {req}</label>
                  <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="ops@tinkerpost.co.th"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Phone")} {req}</label>
                  <input className="input" value={form.phone} onChange={set('phone')} placeholder="02-xxx-xxxx"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("LINE ID")} <span className="muted">({t("optional")})</span></label>
                  <input className="input mono" value={form.line_id} onChange={set('line_id')} placeholder="@yourcompany"/>
                </div>
                <div className="field">
                  <label className="field-label">{isIndividual ? t('National ID') : t('Tax ID')} — {t("13 digits")} {req}</label>
                  <input className="input mono" value={form.tax_id} onChange={set('tax_id')} placeholder="0105561029384"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("City")}</label>
                  <input className="input" value={form.city} onChange={set('city')} placeholder="Bangkok"/>
                </div>
                <div className="field">
                  <label className="field-label">{t("Country")}</label>
                  <input className="input mono" value={form.country} onChange={set('country')} placeholder="TH"/>
                </div>
                <div className="field full">
                  <label className="field-label">{t("Registered address")}</label>
                  <textarea className="input" rows={2} value={form.address} onChange={set('address')}
                    placeholder="140/22 Sukhumvit Soi 31, Wattana, Bangkok 10110"/>
                </div>
                <div className="field full">
                  <label className="field-label">{t("Notes / why this supplier")}</label>
                  <textarea className="input" rows={2} value={form.notes} onChange={set('notes')}
                    placeholder="Referred by Aurora Media. Used for Toyota campaign Q1."/>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>2 · {t("Banking")}</h3>
              <span className="muted" style={{fontSize: 12}}>{t("Collected from the supplier.")}</span>
            </div>
            <div className="section-body">
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {t("Bank account details are now entered by the supplier themselves — they fill them in on the document-upload screen, alongside their KYC documents, via the link you send after creating them. You can still set or correct banking later from the supplier's profile if needed.")}
              </div>
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>3 · {t("Credit limit")}</h3>
              <span className="muted" style={{fontSize: 12}}>{t("Maximum exposure approved by Finance.")}</span>
            </div>
            <div className="section-body">
              <div className="form-grid">
                <div className="field">
                  <label className="field-label">{t("Credit limit (THB)")} {req}</label>
                  <MoneyInput className="input mono" value={form.credit_limit} onChange={set('credit_limit')} placeholder="2,500,000.00"/>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="section-head"><h3>4 · {t("KYC documents")} <span className="muted" style={{fontWeight: 400, fontSize: 13}}>({t("optional here")})</span></h3>
              <span className="muted" style={{fontSize: 12}}>{t("PDF / image · max 20 MB each")}</span>
            </div>
            <div className="section-body">
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
                {t("Attach now if you have them, or skip — after you create the supplier you can send a KYC link for them to upload these themselves.")} <strong>{isIndividual ? t('Individual') : t('Company')} {t("required")}:</strong>{' '}
                {isIndividual ? t('National ID copy + bank book.') : t('Business registration + bank book (VAT optional).')}
              </div>
              <div className="form-grid">
                {isIndividual ? (
                  <>
                    <DocField name="national_id" label={t("National ID copy") + " ⋆"}/>
                    <DocField name="book_bank"   label={t("Bank book copy") + " ⋆"}/>
                  </>
                ) : (
                  <>
                    <DocField name="business_registration" label={t("Business registration") + " ⋆"}/>
                    <DocField name="book_bank"              label={t("Bank book copy") + " ⋆"}/>
                    <DocField name="vat_cert"               label={t("VAT certificate") + " (" + t("optional") + ")"}/>
                    <DocField name="rate_card"              label={t("Rate card") + " (" + t("optional") + ")"}/>
                    <DocField name="company_profile"        label={t("Company profile") + " (" + t("optional") + ")"}/>
                  </>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 14, lineHeight: 1.6 }}>
                {t("⋆ Required for KYC — checked before approval, not at creation. Each uploaded file gets a row in the Compliance tab and lives in this supplier's Drive folder.")}
              </div>
            </div>
          </div>

          {error && (
            <div style={{padding: "12px 14px", background: "color-mix(in oklab, var(--danger) 12%, transparent)", color: "var(--danger)", borderRadius: 8, fontSize: 13}}>
              {error}
            </div>
          )}
        </div>

        <div style={{display:"flex", flexDirection:"column", gap: 16, position: "sticky", top: 24, alignSelf: "flex-start"}}>
          <div className="section">
            <div className="section-head"><h3>{t("What happens next")}</h3></div>
            <div className="section-body" style={{fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6}}>
              {t("On submit, the supplier is created with status review and added to the Approval queue. You'll then be offered a KYC link to copy and email the supplier so they can upload their documents. Any docs you attach here are stored immediately. Procurement approves once the required KYC docs are on file.")}
            </div>
          </div>
          <div className="section">
            <div className="section-head"><h3>{t("KYC checklist")} · {isIndividual ? t('Individual') : t('Company')}</h3></div>
            <div className="section-body" style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.8 }}>
              {isIndividual ? (
                <>
                  ⋆ {t("National ID copy")}<br/>
                  ⋆ {t("Bank book copy")}
                </>
              ) : (
                <>
                  ⋆ {t("Business registration")}<br/>
                  ⋆ {t("Bank book copy")}<br/>
                  ⚬ {t("VAT certificate")} ({t("optional")})<br/>
                  ⚬ {t("Rate card")} ({t("optional")})<br/>
                  ⚬ {t("Company profile")} ({t("optional")})
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SCREEN 4 — Approval / Verification Queue
   ============================================================ */
export function ScreenApproval({ approvals = PENDING_APPROVALS, vendors = [], compliance = [], onDecide }) {
  // ⚠ MODIFIED FROM PORT — index-based selection (immune to duplicate ids),
  // full vendor profile in detail panel, real KYC docs with download.
  const t = useT();
  const [tab, setTab] = useState("vendor");
  const [openIdx, setOpenIdx] = useState(0);
  const [note, setNote] = useState("");
  const [pendingDecision, setPendingDecision] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (openIdx >= approvals.length) setOpenIdx(0);
  }, [approvals.length, openIdx]);
  const open = approvals[openIdx];
  const openVendor = open
    ? vendors.find(v => (v.vendor_id || v.id) === open.vendor_id)
    : null;
  const openDocs = open
    ? compliance.filter(c => c.vendor_id === open.vendor_id && c.drive_file_id)
    : [];

  const DOC_LABEL = {
    'business-registration': 'Business registration',
    'tax_cert':              'VAT certificate',
    'national_id':           'National ID copy',
    'book_bank':             'Bank book copy',
    'rate_card':             'Rate card',
    'company_profile':       'Company profile',
    'insurance':             'Insurance',
    'kyc':                   'KYC document',
    'contract':              'Contract',
    'nda':                   'NDA',
    'wht':                   'Withholding tax',
    'other':                 'Other',
  };

  // ⚠ NEW (2026-05-31) — empty-state copy for the non-vendor approval tabs so
  // they switch and read clearly instead of looking dead. Only "New vendors"
  // has a live queue today; the others are wired for when those flows ship.
  const TAB_EMPTY = {
    bills:   "No billing documents are awaiting approval. Invoice review happens in the Billing inbox.",
    changes: "No supplier profile-change requests are pending review.",
    disputed:"No disputed items right now. Disputed invoices are managed in the Billing inbox.",
  };

  const doDecide = async (decision) => {
    if (!open || pendingDecision) return;
    if (decision === 'rejected' && !note.trim()) {
      window.__toast?.('Add a rejection reason before rejecting.', 'error');
      return;
    }
    setPendingDecision(decision);
    try {
      // Pass vendor_id (always unique) — backend looks up by vendor_id + pending status
      await onDecide?.(open.vendor_id, decision, decision === 'rejected' ? note.trim() : '');
      setNote("");
    } catch (e) {
      // Toast already shown by parent
    } finally {
      setPendingDecision(null);
    }
  };

  const doDownload = async (complianceId) => {
    setDownloadingId(complianceId);
    try { await downloadComplianceDocument(complianceId); }
    catch (e) { window.__toast?.(`Download failed: ${e.message}`, 'error'); }
    finally { setDownloadingId(null); }
  };

  // Auto-check derivation from live data
  // Required KYC docs depend on supplier type (individual → National ID + Bank
  // book; company → Business registration + Bank book, VAT optional).
  const isIndiv = openVendor && openVendor.vendor_type === 'individual';
  const hasDoc = (t) => openDocs.some(d => d.doc_type === t);
  const reqDocChecks = isIndiv
    ? [[t('National ID copy uploaded'), hasDoc('national_id')],
       [t('Bank book uploaded'),        hasDoc('book_bank')]]
    : [[t('Business registration uploaded'), hasDoc('business-registration')],
       [t('Bank book uploaded'),             hasDoc('book_bank')]];
  const checks = open && openVendor ? [
    [t(isIndiv ? 'National ID format valid (13 digits)' : 'Tax ID format valid (13 digits)'),  /^\d{13}$/.test(padThaiId(openVendor.tax_id || openVendor.taxId))],
    [t('Bank account name on file'),          !!String(openVendor.account_name || '').trim()],
    [t('Bank account number on file'),        !!String(openVendor.account_number || '').trim()],
    [t('Credit limit set'),                   Number(openVendor.credit_limit || openVendor.creditLimit) > 0],
    [t('Contact email present'),              !!String(openVendor.email || '').trim()],
    ...reqDocChecks,
  ] : [];

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Approval queue")}</h1>
          <div className="sub">{t("{n} new", { n: approvals.length })} {t("awaiting review · SLA target 24h")}</div>
        </div>
        <div className="page-actions">
          {/* ⚠ MODIFIED FROM PORT — bulk actions removed (non-functional + risky) */}
        </div>
      </div>

      <div className="tabs">
        {[
          ["vendor", "New vendors", approvals.length],
          ["bills", "Billing docs", 0],
          ["changes", "Profile changes", 0],
          ["disputed", "Disputed", 0],
        ].map(([id, lbl, n]) => (
          <button key={id} className={"tab " + (tab === id ? "active" : "")} onClick={() => setTab(id)}>
            {t(lbl)} <span className="muted" style={{marginLeft: 6, fontSize: 11}}>{n}</span>
          </button>
        ))}
      </div>

      <div style={{display: "grid", gridTemplateColumns: "1fr 420px", gap: 0, flex: 1, overflow: "hidden"}}>
        <div style={{overflowY:"auto", borderRight: "1px solid var(--border-subtle)"}}>
          {tab !== "vendor" && (
            <div style={{padding: 40, color: "var(--text-tertiary)", textAlign: "center", fontSize: 13, lineHeight: 1.6}}>
              {t(TAB_EMPTY[tab])}
            </div>
          )}
          {tab === "vendor" && approvals.length === 0 && (
            <div style={{padding: 32, color: "var(--text-tertiary)", textAlign: "center"}}>
              {t('No pending approvals. Register a new supplier to see one here.')}
            </div>
          )}
          {tab === "vendor" && approvals.map((p, idx) => (
            <div key={idx}
              className="approval-row"
              onClick={() => setOpenIdx(idx)}
              style={{
                cursor:"pointer",
                background: idx === openIdx ? "var(--accent-soft)" : "transparent",
                borderLeft: idx === openIdx ? "3px solid var(--accent)" : "3px solid transparent",
              }}>
              <div className="approval-meta">
                <div className="approval-title" style={{display:"flex", alignItems:"center", gap: 10}}>
                  <Avatar name={p.name} size="sm"/>
                  <span>{p.name}</span>
                  <span className="mono muted" style={{fontSize: 11}}>{p.vendor_id || ''}</span>
                </div>
                <div className="approval-sub">
                  {t('Submitted by {who} ({role}) · {when} · {n} docs', { who: p.submittedBy, role: p.role, when: p.when, n: p.docs })}
                </div>
                <div className="approval-chips">
                  <CategoryTag id={p.cat}/>
                  <span className="pill pill-neutral mono">{p.terms}</span>
                </div>
              </div>
              <div className="approval-actions" onClick={(e) => e.stopPropagation()}>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setOpenIdx(idx);
                  // Use a microtask so openIdx update lands first, then doDecide reads the right `open`.
                  Promise.resolve().then(() => doDecide('approved'));
                }} disabled={!!pendingDecision}>
                  <IconCheck size={12}/> {t('Approve')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{overflowY: "auto", padding: 20, background: "var(--bg-secondary)"}}>
          {tab === "vendor" && open && (
            <div style={{display: "flex", flexDirection: "column", gap: 16}}>
              <div style={{display:"flex", gap: 14, alignItems:"center"}}>
                <Avatar name={open.name} size="lg"/>
                <div>
                  <div style={{fontSize: 18, fontWeight: 500}}>{open.name}</div>
                  <div className="muted" style={{fontSize: 12}}><span className="mono">{open.id}</span> · {open.when}</div>
                </div>
              </div>

              <dl className="kv" style={{gridTemplateColumns: "140px 1fr"}}>
                <dt>{t('Submitted by')}</dt><dd>{open.submittedBy} ({open.role})</dd>
                <dt>{t('Category')}</dt><dd><CategoryTag id={open.cat}/></dd>
                <dt>{t('Vendor ID')}</dt><dd className="mono">{open.vendor_id || '—'}</dd>
                <dt>{t('Proposed terms')}</dt><dd className="mono">{open.terms}</dd>
                {openVendor && <>
                  <dt>{t('Contact')}</dt><dd>{openVendor.contact || '—'}</dd>
                  <dt>{t('Email')}</dt><dd className="mono" style={{fontSize: 12}}>{openVendor.email || '—'}</dd>
                  <dt>{t('Phone')}</dt><dd className="mono">{openVendor.phone || '—'}</dd>
                  <dt>{t('Tax ID')}</dt><dd className="mono">{padThaiId(openVendor.tax_id || openVendor.taxId) || '—'}</dd>
                  <dt>{t('City / Country')}</dt><dd>{openVendor.city || '—'}, {openVendor.country || '—'}</dd>
                  <dt>{t('Credit limit')}</dt><dd className="mono">{(openVendor.credit_limit || openVendor.creditLimit) ? `THB ${Number(openVendor.credit_limit || openVendor.creditLimit).toLocaleString()}` : '—'}</dd>
                </>}
                <dt>{t('Submission note')}</dt><dd style={{lineHeight: 1.5}}>{open.note || '—'}</dd>
              </dl>

              {openVendor && (
                <div>
                  <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t('Banking')}</div>
                  <dl className="kv" style={{gridTemplateColumns: "140px 1fr"}}>
                    <dt>{t('Bank')}</dt><dd>{openVendor.bank_name || '—'}</dd>
                    <dt>{t('Account number')}</dt><dd className="mono">{openVendor.account_number || '—'}</dd>
                    <dt>{t('Account name')}</dt><dd>{openVendor.account_name || '—'}</dd>
                  </dl>
                </div>
              )}

              <div>
                <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t('Auto-checks')}</div>
                <div style={{display:"flex", flexDirection:"column", gap: 8}}>
                  {checks.map(([t, ok], i) => (
                    <div key={i} style={{display:"flex", alignItems:"center", gap: 10, fontSize: 13}}>
                      <span style={{
                        width: 18, height: 18, borderRadius: "50%",
                        background: ok ? "color-mix(in oklab, var(--success) 22%, transparent)" : "color-mix(in oklab, var(--warning) 22%, transparent)",
                        color: ok ? "var(--success)" : "var(--warning)",
                        display:"inline-flex", alignItems:"center", justifyContent:"center"
                      }}>{ok ? <IconCheck size={11}/> : "!"}</span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 10}}>{t('KYC documents ({n})', { n: openDocs.length })}</div>
                {openDocs.length === 0 ? (
                  <div style={{padding: 14, color: "var(--text-tertiary)", fontSize: 12, fontStyle: "italic"}}>
                    {t('No KYC documents on file for this vendor.')}
                  </div>
                ) : openDocs.map(d => (
                  <div className="file-row" key={d.compliance_id || d.id}>
                    <div className="file-icon">pdf</div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div className="file-name">{t(DOC_LABEL[d.doc_type] || d.doc_type)}</div>
                      <div className="file-meta">{d.status === 'valid' ? t('On file') : d.status}</div>
                    </div>
                    <button className="icon-btn"
                      title={t('Download')}
                      onClick={() => doDownload(d.compliance_id || d.id)}
                      disabled={downloadingId === (d.compliance_id || d.id)}>
                      {downloadingId === (d.compliance_id || d.id) ? '…' : <IconDownload size={14}/>}
                    </button>
                  </div>
                ))}
              </div>

              <div className="hr"></div>

              <div className="field">
                <label className="field-label">{t('Review note (required for rejection)')}</label>
                <textarea
                  className="input"
                  placeholder={t('Add context, rejection reason, or follow-ups…')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div style={{display:"flex", gap: 8, justifyContent:"flex-end"}}>
                <div style={{display:"flex", gap: 8}}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{color:"var(--danger)"}}
                    disabled={!!pendingDecision}
                    onClick={() => doDecide('rejected')}
                  >
                    <IconX size={14}/> {pendingDecision === 'rejected' ? t('Rejecting…') : t('Reject')}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!!pendingDecision}
                    onClick={() => doDecide('approved')}
                  >
                    <IconCheck size={14}/> {pendingDecision === 'approved' ? t('Approving…') : t('Approve vendor')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SCREEN 5 — Quality Scoring
   ============================================================ */
// ⚠ MODIFIED FROM PORT — live ratings + vendors, working filters + New review.
export function ScreenQuality({ ratings = [], vendors = [], onNewReview }) {
  const t = useT();
  const [filterCat, setFilterCat] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const vendorById = useMemo(() => {
    const m = {};
    (vendors || []).forEach(v => { m[v.vendor_id || v.id] = v; });
    return m;
  }, [vendors]);
  const filteredRatings = useMemo(() => ratings.filter(r => {
    const v = vendorById[r.vendor_id];
    if (filterCat !== 'all' && (!v || v.cat !== filterCat)) return false;
    if (minRating > 0 && Number(r.overall_5) < minRating) return false;
    return true;
  }), [ratings, vendorById, filterCat, minRating]);
  const totalReviews = filteredRatings.length;
  const avgScore = filteredRatings.length
    ? +(filteredRatings.reduce((a, r) => a + (Number(r.overall_5) || 0), 0) / filteredRatings.length).toFixed(1)
    : 0;
  const uniqueVendors = new Set(filteredRatings.map(r => r.vendor_id)).size;
  const flagged = vendors.filter(v => Number(v.rating) > 0 && Number(v.rating) < 3).length;
  const recentAll = [...filteredRatings].sort((a, b) =>
    String(b.rated_at || '').localeCompare(String(a.rated_at || ''))
  );
  const recent = recentAll.slice(0, 10);
  // ⚠ Featured "open review" — the most recent review WITHIN the current
  // filters, so changing category/rating updates the highlighted supplier.
  const featured = recentAll[0] || null;
  const featRows = featured ? [
    [t("Quality of work"), Number(featured.quality_5) || 0],
    [t("Timeliness"),      Number(featured.timeliness_5) || 0],
    [t("Communication"),   Number(featured.comms_5) || 0],
    [t("Value for money"), Number(featured.value_5) || 0],
  ] : [];
  const featOverall = featured ? (Number(featured.overall_5) || 0) : 0;
  return (
    <div className="page">
      <div className="page-header">
        <div className="page-title-group">
          <h1>{t("Quality reviews")}</h1>
          <div className="sub">{t("PMs and AEs score suppliers at project closure · rolling 12-month average")}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary btn-sm" onClick={() => onNewReview?.()}><IconPlus size={14}/> {t("New review")}</button>
        </div>
      </div>

      {/* ⚠ MODIFIED FROM PORT — filter bar (category + min rating) */}
      <div className="toolbar">
        <span className="muted" style={{fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em"}}>{t('Filters')}</span>
        <select className="chip" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{paddingRight: 22}}>
          <option value="all">{t('All categories')}</option>
          {CATEGORIES.map(c => <option key={c.id} value={c.id}>{t(c.label)}</option>)}
        </select>
        <select className="chip" value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} style={{paddingRight: 22}}>
          <option value={0}>{t('Any rating')}</option>
          <option value={3}>{t('3.0 and up')}</option>
          <option value={4}>{t('4.0 and up')}</option>
          <option value={4.5}>{t('4.5 and up')}</option>
        </select>
        {(filterCat !== 'all' || minRating > 0) && (
          <button className="chip" onClick={() => { setFilterCat('all'); setMinRating(0); }}>{t('Clear')}</button>
        )}
      </div>

      <div className="stat-row">
        <div className="stat-tile">
          <div className="label">{t('Reviews submitted')}</div>
          <div className="val">{totalReviews}</div>
          <div className="delta">{t('across all engagements')}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Average score')}</div>
          <div className="val">{avgScore || '—'}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>/5</span></div>
          <div className="delta">{t('{n} reviews scored', { n: totalReviews })}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Suppliers reviewed')}</div>
          <div className="val">{uniqueVendors}<span style={{fontSize:18, color:"var(--text-tertiary)"}}>/{vendors.length || '—'}</span></div>
          <div className="delta">{vendors.length ? t('{n}% coverage', { n: Math.round(uniqueVendors / vendors.length * 100) }) : '—'}</div>
        </div>
        <div className="stat-tile">
          <div className="label">{t('Flagged for review')}</div>
          <div className="val">{flagged}</div>
          <div className="delta down">{t('Score < 3.0')}</div>
        </div>
      </div>

      <div style={{padding: "0 24px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16}}>
        <div className="section">
          <div className="section-head"><h3>{featured ? t('Open review — {name}', { name: featured.vendor || featured.vendor_name || featured.vendor_id }) : t('No reviews')}</h3>
            <span className="muted" style={{fontSize: 11}}>
              {featured ? `${featured.project || '—'}${featured.rated_at ? ` · ${new Date(featured.rated_at).toLocaleDateString()}` : ''}` : t('Matching the current filters')}
            </span>
          </div>
          {!featured ? (
            <div style={{padding: 40, color: "var(--text-tertiary)", textAlign: "center", fontSize: 13}}>
              {t('No reviews match these filters.')}
            </div>
          ) : (
            <>
              <div className="score-grid">
                <div className="score-num">
                  <div className="n">{featOverall.toFixed(1)}<span className="denom">/5</span></div>
                  {featOverall >= 4.5 && <div className="grade"><IconStar size={10}/> {t('Preferred supplier')}</div>}
                  <div className="muted" style={{fontSize: 11, marginTop: 4}}>
                    {t('Submitted by {who}', { who: featured.rater || (featured.rated_by_email || '').split('@')[0] || '—' })}
                  </div>
                </div>
                <div className="score-rows">
                  {featRows.map(([l, v]) => (
                    <div className="score-row" key={l}>
                      <div className="label">{l}</div>
                      <div className="bar"><div className="fill" style={{width: (v / 5 * 100) + "%"}}></div></div>
                      <div className="val">{v.toFixed(1)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{padding: "0 18px 18px"}}>
                <div className="field-label" style={{marginBottom: 8}}>{t('PM comment')}</div>
                <div style={{
                  background: "var(--bg-primary)", padding: 14, borderRadius: "var(--radius-md)",
                  fontSize: 13, lineHeight: 1.6, color: "var(--text-secondary)",
                  border: "1px solid var(--border-subtle)"
                }}>
                  {featured.comment ? `"${featured.comment}"` : <span style={{fontStyle:"italic", color:"var(--text-tertiary)"}}>{t('No comment left for this review.')}</span>}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="section">
          <div className="section-head"><h3>{t('Recent reviews')}</h3>
            <span className="muted" style={{fontSize: 11}}>{t('{a} of {b} rows', { a: recent.length, b: filteredRatings.length })}</span>
          </div>
          {recent.length === 0 ? (
            <div style={{padding: 32, color: "var(--text-tertiary)", textAlign: "center", fontSize: 13}}>
              {t('No reviews match these filters.')}
            </div>
          ) : (
            <table className="tbl" style={{tableLayout: "auto"}}>
              <thead>
                <tr>
                  <th>{t('Supplier')}</th>
                  <th>{t('Project')}</th>
                  <th>{t('Score')}</th>
                  <th>{t('Reviewer')}</th>
                  <th>{t('Date')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const score = Number(r.overall_5) || 0;
                  return (
                    <tr key={r.rating_id || r.id}>
                      <td><div style={{display:"flex", gap: 8, alignItems:"center"}}>
                        <Avatar name={r.vendor || r.vendor_name || r.vendor_id} size="sm"/>
                        {r.vendor || r.vendor_name || r.vendor_id}
                      </div></td>
                      <td className="muted">{r.project || '—'}</td>
                      <td>
                        <span className="rating">
                          <span className="rating-bar">
                            {Array.from({length: 5}).map((_, j) => (
                              <i key={j} className={j < Math.round(score) ? "on" : ""}></i>
                            ))}
                          </span>
                          <span className="tnum" style={{fontSize: 12, color: score < 3 ? "var(--danger)" : "var(--text-secondary)"}}>{score.toFixed(1)}</span>
                        </span>
                      </td>
                      <td className="muted">{r.rater || (r.rated_by_email || '').split('@')[0]}</td>
                      <td className="muted mono" style={{fontSize: 12}}>{r.rated_at ? new Date(r.rated_at).toLocaleDateString() : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="section" style={{gridColumn: "1 / -1"}}>
          <div className="section-head"><h3>{t('Flagged — score < 3.0')}</h3>
            <span className="muted" style={{fontSize: 11}}>{t('Live from the Vendors master · {n} vendors', { n: flagged })}</span>
          </div>
          {flagged === 0 ? (
            <div style={{padding: 24, color: "var(--text-tertiary)", textAlign: "center", fontSize: 13}}>
              {t('No flagged vendors. All active suppliers currently rated 3.0 or above.')}
            </div>
          ) : (
            <div className="section-body" style={{display:"grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12}}>
              {vendors.filter(v => Number(v.rating) > 0 && Number(v.rating) < 3).slice(0, 8).map(f => (
                <div key={f.vendor_id || f.id} style={{padding: 14, border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)"}}>
                  <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8}}>
                    <div style={{fontSize: 13, fontWeight: 400}}>{f.name}</div>
                    <span style={{fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--danger)", fontWeight: 500}}>{Number(f.rating).toFixed(1)}</span>
                  </div>
                  <div style={{fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.5}}>
                    {t((catOf(f.cat) || {}).label || f.cat || '')} · {t('{n} projects', { n: f.projects || 0 })} · {f.contact || '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

